<template>
  <div
    class="tool-container"
    :style="{ '--attribute-table-height': attributeTableHeight + 'px' }"
  >
    <div class="tool-layout" @map-tool-position-change="handlePanelPositionChange">
      <!-- QA-204: sin panel visual — SelectTool queda montado sin UI para la
           consulta por punto implícita (popup al clickear una capa activa). -->
      <SelectTool />

      <!-- Simbología conserva su estado de control, pero comparte la distribución. -->
      <div
        v-show="legendsActive"
        class="tool-slot"
        data-tool-id="legends"
        :style="panelStyle('legends')"
        @mousedown.capture="bringToFront('legends')"
      >
        <Legends
          class="tool-panel--docked"
          :is-active="legendsActive"
          :is-fixed="false"
          @onLegendAction="handleLegendAction"
        />
      </div>

      <div v-if="mapToolValues('layers').active" class="tool-slot" data-tool-id="layers" :style="panelStyle('layers')" @mousedown.capture="bringToFront('layers')">
        <LayersTool class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('shpKmzUpload').active" class="tool-slot" data-tool-id="shpKmzUpload" :style="panelStyle('shpKmzUpload')" @mousedown.capture="bringToFront('shpKmzUpload')">
        <UploadShpKmz class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('search').active" class="tool-slot" data-tool-id="search" :style="panelStyle('search')" @mousedown.capture="bringToFront('search')">
        <Search class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('locator').active" class="tool-slot" data-tool-id="locator" :style="panelStyle('locator')" @mousedown.capture="bringToFront('locator')">
        <LocatorTool class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('annotations').active" class="tool-slot" data-tool-id="annotations" :style="panelStyle('annotations')" @mousedown.capture="bringToFront('annotations')">
        <Annotations class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('basemap').active" class="tool-slot" data-tool-id="basemap" :style="panelStyle('basemap')" @mousedown.capture="bringToFront('basemap')">
        <ChangeBasemap class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('geoprocessing').active" class="tool-slot" data-tool-id="geoprocessing" :style="panelStyle('geoprocessing')" @mousedown.capture="bringToFront('geoprocessing')">
        <Geoprocessings class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('measures').active" class="tool-slot" data-tool-id="measures" :style="panelStyle('measures')" @mousedown.capture="bringToFront('measures')">
        <MeasureTool class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('draw').active" class="tool-slot" data-tool-id="draw" :style="panelStyle('draw')" @mousedown.capture="bringToFront('draw')">
        <DrawTool class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('help').active" class="tool-slot" data-tool-id="help" :style="panelStyle('help')" @mousedown.capture="bringToFront('help')">
        <HelpDocuments class="tool-panel--docked" />
      </div>

      <div v-if="mapToolValues('wmsUpload').active" class="tool-slot" data-tool-id="wmsUpload" :style="panelStyle('wmsUpload')" @mousedown.capture="bringToFront('wmsUpload')">
        <WmsUpload class="tool-panel--docked" />
      </div>
    </div>

    <!-- Print: siempre montado (patrón modal + watch isActive, ver Print.vue) -->
    <Print />
  </div>
</template>
<script>
import { vuex } from '@/components/mixin/vuex.js'

// Import all available tools here
// Future tools can be added to this map
import SelectTool from '@/components/mapTools/select/Select.vue'
import Legends from '@/components/mapControls/legends/Legends.vue'
import LayersTool from '@/components/mapTools/layers/Layers.vue'
import UploadShpKmz from '@/components/mapTools/UploadShpKmz/UploadShpKmz.vue'
import Search from '@/components/mapTools/search/Search.vue'
import Annotations from '@/components/mapTools/annotations/Annotations.vue'
import ChangeBasemap from '@/components/mapTools/baseMap/ChangeBasemap.vue'
import Geoprocessings from '@/components/mapTools/geoprocessings/Geoprocessings.vue'
import MeasureTool from '@/components/mapTools/measures/Measure.vue'
import LocatorTool from './locator/Locator.vue'
import DrawTool from './draw/DrawTool.vue'
import HelpDocuments from '@/components/mapTools/help/HelpDocuments.vue'
import Print from '@/components/mapTools/print/Print.vue'
import WmsUpload from '@/components/mapTools/wmsUpload/WmsUpload.vue'

export default {
  name: 'ToolContainer',
  mixins: [vuex],
  components: {
    SelectTool,
    Legends,
    LayersTool,
    UploadShpKmz,
    Search,
    Annotations,
    ChangeBasemap,
    Geoprocessings,
    MeasureTool,
    LocatorTool,
    DrawTool,
    HelpDocuments,
    Print,
    WmsUpload
  },
  data() {
    return {
      panelPositions: {},
      panelZIndex: 1000,
      fallbackSequence: 0
    }
  },
  computed: {
    attributeTableHeight() {
      return this.$store.getters['attributeTable/layoutHeight'] || 0
    },
    legendsActive() {
      const legends = this.mapControlValues('legends')
      return !!(legends && legends.active)
    },
    activePanelIds() {
      const ids = []
      const tools = [
        'layers',
        'shpKmzUpload',
        'search',
        'locator',
        'annotations',
        'basemap',
        'geoprocessing',
        'measures',
        'draw',
        'help',
        'wmsUpload'
      ]

      if (this.legendsActive) ids.push('legends')
      tools.forEach(id => {
        if (this.mapToolValues(id).active) ids.push(id)
      })

      return ids
    }
  },
  watch: {
    activePanelIds: {
      immediate: true,
      handler(currentIds, previousIds) {
        const previous = previousIds || []
        const closed = previous.filter(id => !currentIds.includes(id))
        const opened = currentIds.filter(id => !previous.includes(id))

        closed.forEach(id => this.clearPanelPosition(id))

        if (opened.length) {
          this._placementQueue = (this._placementQueue || Promise.resolve())
            .then(() => this.placeOpenedPanels(opened))
        }
      }
    }
  },
  methods: {
    handleLegendAction(component, action) {
      if (action === 'exit') {
        this.selectMapControl('legends', false)
      }
    },
    panelStyle(id) {
      const position = this.panelPositions[id]
      if (!position) return { visibility: 'hidden' }

      return {
        left: `${position.left}px`,
        top: `${position.top}px`,
        zIndex: position.zIndex,
        visibility: 'visible'
      }
    },
    panelSlot(id) {
      if (!this.$el) return null
      return this.$el.querySelector(`.tool-slot[data-tool-id="${id}"]`)
    },
    clearPanelPosition(id) {
      const slot = this.panelSlot(id)
      if (slot) {
        slot.style.removeProperty('left')
        slot.style.removeProperty('top')
        slot.style.removeProperty('z-index')
      }
      this.$delete(this.panelPositions, id)
    },
    async placeOpenedPanels(ids) {
      for (const id of ids) {
        await this.$nextTick()
        if (!this.activePanelIds.includes(id) || this.panelPositions[id]) continue
        this.placePanel(id)
      }
    },
    placePanel(id) {
      const layout = this.$el && this.$el.querySelector('.tool-layout')
      const slot = this.panelSlot(id)
      if (!layout || !slot) return

      const layoutRect = layout.getBoundingClientRect()
      const slotRect = slot.getBoundingClientRect()
      const width = Math.min(slotRect.width || 360, Math.max(layoutRect.width - 16, 1))
      const height = Math.min(slotRect.height || 220, Math.max(layoutRect.height - 80, 1))
      const edge = 8
      const headerSpace = window.innerWidth <= 700 ? 72 : 64
      const rightInset = window.innerWidth <= 1100
        ? 16
        : Math.min(190, Math.max(80, window.innerWidth * 0.1))
      const minX = edge
      const maxX = Math.max(minX, layoutRect.width - width - edge)
      const minY = Math.min(headerSpace, Math.max(layoutRect.height - height - edge, edge))
      const maxY = Math.max(minY, layoutRect.height - height - edge)
      const occupied = this.currentPanelRects(id, layoutRect)
      const preferredTop = id === 'geoprocessing' && occupied.length === 0
        ? minY
        : this.clamp((layoutRect.height - height) / 2, minY, maxY)
      const preferred = {
        left: this.clamp(layoutRect.width - rightInset - width, minX, maxX),
        top: preferredTop
      }
      const freePosition = this.findFreePosition({
        preferred,
        occupied,
        width,
        height,
        minX,
        maxX,
        minY,
        maxY
      })
      const position = freePosition || this.fallbackPosition(preferred, minX, maxX, minY, maxY)

      this.panelZIndex += 1
      this.$set(this.panelPositions, id, {
        left: Math.round(position.left),
        top: Math.round(position.top),
        zIndex: this.panelZIndex
      })
    },
    currentPanelRects(excludedId, layoutRect) {
      return this.activePanelIds
        .filter(id => id !== excludedId && this.panelPositions[id])
        .map(id => this.panelSlot(id))
        .filter(Boolean)
        .map(slot => {
          const rect = slot.getBoundingClientRect()
          return {
            left: rect.left - layoutRect.left,
            right: rect.right - layoutRect.left,
            top: rect.top - layoutRect.top,
            bottom: rect.bottom - layoutRect.top
          }
        })
    },
    findFreePosition({ preferred, occupied, width, height, minX, maxX, minY, maxY }) {
      if (!occupied.length) return preferred

      const gap = 16
      const xValues = [preferred.left, minX, maxX]
      const yValues = [preferred.top, minY, maxY]

      occupied.forEach(rect => {
        xValues.push(rect.left - gap - width, rect.right + gap)
        yValues.push(rect.top, rect.bottom + gap, rect.top - gap - height)
      })

      // Completa los huecos menos evidentes sin imponer un máximo de paneles.
      for (let x = maxX; x >= minX; x -= 32) xValues.push(x)
      for (let offset = 0; offset <= maxY - minY; offset += 32) {
        yValues.push(preferred.top - offset, preferred.top + offset)
      }

      const candidates = []
      xValues.forEach(left => {
        yValues.forEach(top => {
          if (left < minX || left > maxX || top < minY || top > maxY) return
          candidates.push({ left, top })
        })
      })

      candidates.sort((a, b) => {
        const distanceA = Math.abs(a.left - preferred.left) + Math.abs(a.top - preferred.top)
        const distanceB = Math.abs(b.left - preferred.left) + Math.abs(b.top - preferred.top)
        return distanceA - distanceB
      })

      return candidates.find(candidate => {
        const rect = {
          left: candidate.left,
          right: candidate.left + width,
          top: candidate.top,
          bottom: candidate.top + height
        }
        return occupied.every(other => !this.rectsOverlap(rect, other, gap))
      }) || null
    },
    rectsOverlap(a, b, gap) {
      return a.left < b.right + gap &&
        a.right + gap > b.left &&
        a.top < b.bottom + gap &&
        a.bottom + gap > b.top
    },
    fallbackPosition(preferred, minX, maxX, minY, maxY) {
      const step = 26
      const offset = (this.fallbackSequence % 8) * step
      this.fallbackSequence += 1
      return {
        left: this.clamp(preferred.left - offset, minX, maxX),
        top: this.clamp(preferred.top + offset, minY, maxY)
      }
    },
    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    bringToFront(id) {
      // Un clic dentro del panel también reclama la interacción del mapa; va
      // antes del early return porque un panel sin posición asignada igual
      // debe poder tomar prioridad. Los ids fuera de las cuatro se ignoran
      // dentro de la mutación.
      this.claimMapInteraction(id)
      const position = this.panelPositions[id]
      if (!position) return
      this.panelZIndex += 1
      this.$set(this.panelPositions, id, {
        ...position,
        zIndex: this.panelZIndex
      })
    },
    handlePanelPositionChange(event) {
      const detail = event.detail || {}
      const position = this.panelPositions[detail.id]
      if (!position || !Number.isFinite(detail.left) || !Number.isFinite(detail.top)) return

      this.$set(this.panelPositions, detail.id, {
        ...position,
        left: detail.left,
        top: detail.top
      })
    }
  }
}
</script>

<style scoped>
.tool-container {
  --tool-layout-gap: 16px;
  --tool-layout-right: clamp(80px, 10vw, 190px);
  --attribute-table-height: 0px;

  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

/* Print vive fuera del layout de paneles y debe conservar interacción propia. */
.tool-container > :not(.tool-layout) {
  pointer-events: auto;
}

.tool-layout {
  position: absolute;
  top: 0;
  right: 0;
  bottom: var(--attribute-table-height);
  left: 0;
  overflow: visible;
  pointer-events: none;
}

.tool-slot {
  position: absolute;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - var(--attribute-table-height) - 5rem);
  pointer-events: none;
}

/*
 * Las ventanas conservan tamaños, contenido y arrastre propios. Solo se anulan
 * sus coordenadas iniciales para que el layout decida dónde nacen.
 */
.tool-slot > .tool-panel--docked {
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  left: auto;
  margin: 0;
  max-height: calc(100vh - var(--attribute-table-height) - 5rem);
  pointer-events: auto;
}

@media (max-width: 1100px) {
  .tool-container {
    --tool-layout-gap: 10px;
    --tool-layout-right: 16px;
  }
}

@media (max-width: 700px) {
  .tool-slot {
    width: calc(100% - 16px);
    max-width: calc(100% - 16px);
  }

  .tool-slot > .tool-panel--docked {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: calc(100vh - var(--attribute-table-height) - 5rem);
  }
}
</style>
